import { useCallback, useContext } from 'react';

import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';

import mergeFilterInputs from '../../../mergeFilterInputs';
import getSanitizedEntityFields from '../getSanitizedEntityFields';

const useBuildSearchArgs = () => {
  const { datasets, performanceDatasets } = useContext(
    DatasetDefinitionsContext,
  );

  const buildSearchArgs = useCallback(
    ({
      entity,
      searchText,
      isActiveFilterDisabled,
    }: {
      entity: EntityDetails.Entity;
      searchText: string;
      isActiveFilterDisabled: boolean;
    }): GlobalSearch.SearchArgs | undefined => {
      // Guard against drift between the persisted entity definition and the
      // dataset that backs the query: prune stale secondary/context fields and
      // skip the entity entirely when its primary field no longer exists.
      const datasetDefinition = [...datasets, ...performanceDatasets].find(
        (d) => d.type === entity.entityDataset,
      );
      const { isPrimaryFieldValid, secondaryFields, contextFields } =
        getSanitizedEntityFields({ entity, datasetDefinition });

      if (!isPrimaryFieldValid) {
        return undefined;
      }

      const groupBy: V5GroupBy = {
        field: entity.primaryField,
        type: 'text',
      };

      const secondaryMetrics: MetricInput[] = secondaryFields.map((sf) => ({
        id: sf,
        field: sf,
        dataType: entity.entityDataset,
        aggFunc: 'last',
        filters: {},
      }));
      const contextMetrics: MetricInput[] = contextFields.map((sf) => ({
        id: sf,
        field: sf,
        dataType: entity.entityDataset,
        aggFunc: 'last',
        filters: {},
      }));

      const searchFilter: FilterInput = {
        wildcardFilters: [
          { field: entity.primaryField, pattern: `*${searchText}*` },
        ],
      };
      const filters = isActiveFilterDisabled
        ? [searchFilter]
        : [mergeFilterInputs(entity.isActiveFilter, searchFilter)];

      return {
        groupBy,
        secondaryMetrics,
        contextMetrics,
        filters,
      };
    },
    [datasets, performanceDatasets],
  );

  return buildSearchArgs;
};

export default useBuildSearchArgs;
